import { useState, useEffect } from "react";
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  ResponsiveContainer,
} from "recharts";
import { MdAnalytics } from "react-icons/md";
import { PerformanceService } from "../../../../application/services/performanceService";

const performanceService = new PerformanceService();

const COLORS = [
  "#00C2FF",
  "#FFB800",
  "#FF6B6B",
  "#51CF66",
  "#FF8787",
  "#845EF7",
  "#FCC419",
  "#20C997",
];

const RadialProgressBar = ({ title, value, color }) => {
  const data = [
    {
      name: title,
      value: value,
      fill: color,
    },
  ];

  return (
    <div className="flex flex-col items-center">
      <div className="w-[120px] h-[120px] sm:w-[140px] sm:h-[140px] md:w-[160px] md:h-[160px]">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius="60%"
            outerRadius="90%"
            data={data}
            startAngle={0}
            endAngle={360}
          >
            <PolarAngleAxis
              type="number"
              domain={[0, 100]}
              angleAxisId={0}
              tick={false}
            />
            <RadialBar
              background
              clockWise={true}
              dataKey="value"
              cornerRadius={10}
              fill={color}
            />
            <text
              x="50%"
              y="48%"
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-lg sm:text-2xl font-bold"
              fill="#ffffff"
            >
              {value}%
            </text>
            <text
              x="50%"
              y="62%"
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-[10px] sm:text-xs"
              fill="#9CA3AF"
            >
              {title}
            </text>
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const PerformanceSummary = ({ vmIds, startDate, endDate }) => {
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (startDate && endDate && vmIds && vmIds.length > 0) {
      fetchSummaryData();
    }
  }, [startDate, endDate, vmIds]);

  const fetchSummaryData = async () => {
    setLoading(true);
    setError(null);
    try {
      const results = [];

      for (const vmId of vmIds) {
        const result = await performanceService.getVmPerformanceSummary(
          vmId,
          startDate,
          endDate,
        );
        if (result.success && result.data) {
          results.push({
            vmId: result.data.vmId,
            vmName: result.data.vmName,
            avgMaxCpu: Math.round(result.data.avgMaxCpu * 100) / 100,
            avgCpu: Math.round(result.data.avgCpu * 100) / 100,
            avgMaxRamPercentage:
              Math.round(result.data.avgMaxRamPercentage * 100) / 100,
            avgRamPercentage:
              Math.round(result.data.avgRamPercentage * 100) / 100,
          });
        }
      }

      setSummaryData(results);
    } catch (err) {
      console.error("Error fetching summary data:", err);
      setError("Failed to load performance summary");
    } finally {
      setLoading(false);
    }
  };

  if (!vmIds || vmIds.length === 0) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
        <div className="text-center py-6 sm:py-8">
          <p className="text-gray-400 text-sm sm:text-base">No VM selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
      <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
        <MdAnalytics className="text-primary-cyan text-xl sm:text-2xl" />
        <h3 className="text-lg sm:text-xl font-semibold text-white">Performance Summary</h3>
      </div>

      <div className="mt-4 sm:mt-6">
        {loading ? (
          <div className="text-center py-6 sm:py-8">
            <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-3 text-sm sm:text-base">Loading summary data...</p>
          </div>
        ) : error ? (
          <div className="text-center py-6 sm:py-8">
            <p className="text-red-400 text-sm sm:text-base">{error}</p>
          </div>
        ) : summaryData.length === 0 ? (
          <div className="text-center py-6 sm:py-8">
            <p className="text-gray-400 text-sm sm:text-base">No summary data available for selected period</p>
          </div>
        ) : (
          <div className="space-y-6 sm:space-y-8">
            {summaryData.map((vm) => (
              <div key={vm.vmId} className="border border-gray-700 rounded-xl p-4 sm:p-6">
                <h4 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-6 break-words">
                  {vm.vmName}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                  <RadialProgressBar
                    title="Avg Max CPU"
                    value={vm.avgMaxCpu}
                    color={COLORS[0]}
                  />
                  <RadialProgressBar
                    title="Avg CPU"
                    value={vm.avgCpu}
                    color={COLORS[3]}
                  />
                  <RadialProgressBar
                    title="Avg Max RAM"
                    value={vm.avgMaxRamPercentage}
                    color={COLORS[1]}
                  />
                  <RadialProgressBar
                    title="Avg RAM"
                    value={vm.avgRamPercentage}
                    color={COLORS[2]}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceSummary;